'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';


const steps = [
  { id: 1, title: 'Personal Information', desc: 'Basic profile details' },
  { id: 2, title: 'Professional Details', desc: 'Experience & specialties' },
  { id: 3, title: 'Pricing & Availability', desc: 'Rates and schedule' },
  { id: 4, title: 'Documents', desc: 'Verification documents' },
  { id: 5, title: 'Review & Submit', desc: 'Final review' },
];

export default function BecomeGuidePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', dob: '', gender: '',
    province: '', district: '', municipality: '',
    bio: '', experience: '', category: '', languages: [] as string[],
    specialties: [] as string[], destinations: [] as string[],
    dailyRate: '', hourlyRate: '', licenceNo: '',
    firstAid: false, insurance: false,
  });

  const update = (field: string, value: unknown) => setForm((f) => ({ ...f, [field]: value }));

  const languageOptions = ['English', 'Nepali', 'Hindi', 'French', 'German', 'Spanish', 'Japanese', 'Chinese', 'Tibetan'];
  const categoryOptions = ['Trekking', 'Cultural', 'Wildlife', 'Religious', 'Adventure', 'Photography', 'Luxury'];
  const destinationOptions = ['Everest Region', 'Annapurna', 'Mustang', 'Chitwan', 'Pokhara', 'Kathmandu Valley', 'Lumbini', 'Rara Lake', 'Langtang'];

  const toggleArray = (field: 'languages' | 'specialties' | 'destinations', value: string) => {
    const arr = form[field] as string[];
    update(field, arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);
  };

  return (
    <main className="min-h-screen bg-navy">
      <Header />

      {/* Hero */}
      <div className="pt-24 pb-10 px-4 text-center" style={{ background: 'linear-gradient(180deg, #0E3570 0%, #0B2D5B 100%)' }}>
        <div className="section-eyebrow mb-2">Join Our Community</div>
        <h1 className="heading-display text-3xl md:text-5xl text-snow mb-3">
          Become a <span className="italic text-gold">Verified Guide</span>
        </h1>
        <p className="text-snow/60 text-sm max-w-lg mx-auto">
          Share your knowledge of Nepal with travellers from around the world. Set your own rates, manage your schedule, and grow your guiding business.
        </p>

        {/* Benefits */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          {['Earn Rs. 3,000–8,000/day', 'Set your own schedule', 'Global tourist reach', 'Secure payments'].map((b) => (
            <div key={b} className="flex items-center gap-2 text-sm text-snow/70">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4A017" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {b}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-10 overflow-x-auto pb-2">
          {steps.map((step, i) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    currentStep === step.id
                      ? 'bg-gold text-navy'
                      : currentStep > step.id
                      ? 'bg-sky text-navy' :'bg-white/10 text-snow/40'
                  }`}
                >
                  {currentStep > step.id ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : step.id}
                </div>
                <div className="text-center hidden md:block">
                  <div className={`text-xs font-medium ${currentStep === step.id ? 'text-gold' : 'text-snow/40'}`}>{step.title}</div>
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-px mx-2 ${currentStep > step.id ? 'bg-sky' : 'bg-white/10'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form */}
        <div className="yn-card rounded-2xl p-6 md:p-8">
          {currentStep === 1 && (
            <div>
              <h2 className="text-snow font-bold text-xl mb-6">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Full Name', field: 'fullName', placeholder: 'Your full legal name', type: 'text' },
                  { label: 'Email Address', field: 'email', placeholder: 'your@email.com', type: 'email' },
                  { label: 'Phone Number', field: 'phone', placeholder: '+977-XXXXXXXXXX', type: 'tel' },
                  { label: 'Date of Birth', field: 'dob', placeholder: '', type: 'date' },
                ].map((f) => (
                  <div key={f.field}>
                    <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">{f.label}</label>
                    <input
                      type={f.type}
                      value={form[f.field as keyof typeof form] as string}
                      onChange={(e) => update(f.field, e.target.value)}
                      placeholder={f.placeholder}
                      className="yn-input"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Gender</label>
                  <select value={form.gender} onChange={(e) => update('gender', e.target.value)} className="yn-select w-full px-4 py-3 rounded-xl text-sm">
                    <option value="">Select gender</option>
                    <option>Male</option><option>Female</option><option>Other</option><option>Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Province</label>
                  <select value={form.province} onChange={(e) => update('province', e.target.value)} className="yn-select w-full px-4 py-3 rounded-xl text-sm">
                    <option value="">Select province</option>
                    {['Koshi', 'Madhesh', 'Bagmati', 'Gandaki', 'Lumbini', 'Karnali', 'Sudurpashchim'].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Short Biography</label>
                <textarea
                  value={form.bio}
                  onChange={(e) => update('bio', e.target.value)}
                  placeholder="Tell tourists about yourself, your background, and what makes you a great guide..."
                  rows={4}
                  className="yn-input resize-none"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h2 className="text-snow font-bold text-xl mb-6">Professional Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Years of Experience</label>
                  <input type="number" value={form.experience} onChange={(e) => update('experience', e.target.value)} placeholder="e.g. 8" className="yn-input" />
                </div>
                <div>
                  <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Guide Category</label>
                  <select value={form.category} onChange={(e) => update('category', e.target.value)} className="yn-select w-full px-4 py-3 rounded-xl text-sm">
                    <option value="">Select category</option>
                    {categoryOptions.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="mb-5">
                <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Languages Spoken</label>
                <div className="flex flex-wrap gap-2">
                  {languageOptions.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => toggleArray('languages', l)}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                        form.languages.includes(l) ? 'bg-gold text-navy border-gold font-semibold' : 'border-white/15 text-snow/60 hover:border-gold/40'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Destinations Covered</label>
                <div className="flex flex-wrap gap-2">
                  {destinationOptions.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleArray('destinations', d)}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                        form.destinations.includes(d) ? 'bg-sky/20 text-sky border-sky/50' : 'border-white/15 text-snow/60 hover:border-sky/30'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h2 className="text-snow font-bold text-xl mb-6">Pricing & Availability</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Daily Rate (NPR)</label>
                  <input type="number" value={form.dailyRate} onChange={(e) => update('dailyRate', e.target.value)} placeholder="e.g. 3500" className="yn-input" />
                </div>
                <div>
                  <label className="text-snow/60 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Hourly Rate (NPR)</label>
                  <input type="number" value={form.hourlyRate} onChange={(e) => update('hourlyRate', e.target.value)} placeholder="e.g. 500" className="yn-input" />
                </div>
              </div>
              <div className="yn-card rounded-xl p-4 text-center">
                <div className="text-snow/50 text-sm mb-2">Availability Calendar</div>
                <p className="text-snow/30 text-xs">Full availability calendar will be available after profile approval.</p>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div>
              <h2 className="text-snow font-bold text-xl mb-6">Documents & Verification</h2>
              <div className="space-y-4">
                {[
                  { label: 'Government Guide Licence', required: true, desc: 'Nepal Tourism Board issued guide licence' },
                  { label: 'Citizenship / Passport', required: true, desc: 'Valid government-issued identity document' },
                  { label: 'Training Certificates', required: false, desc: 'Trekking, first aid, or other relevant certificates' },
                  { label: 'Profile Photo', required: true, desc: 'Clear, professional headshot' },
                ].map((doc) => (
                  <div key={doc.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <span className="text-snow font-medium text-sm">{doc.label}</span>
                        {doc.required && <span className="text-gold text-xs ml-2">*Required</span>}
                      </div>
                    </div>
                    <p className="text-snow/40 text-xs mb-3">{doc.desc}</p>
                    <label className="flex items-center gap-2 cursor-pointer btn-navy px-4 py-2 rounded-lg text-sm w-fit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      Upload File
                      <input type="file" className="hidden" />
                    </label>
                  </div>
                ))}

                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
                  <input type="checkbox" id="firstAid" checked={form.firstAid} onChange={(e) => update('firstAid', e.target.checked)} className="accent-gold w-4 h-4" />
                  <label htmlFor="firstAid" className="text-snow/70 text-sm cursor-pointer">I have completed first aid training</label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
                  <input type="checkbox" id="insurance" checked={form.insurance} onChange={(e) => update('insurance', e.target.checked)} className="accent-gold w-4 h-4" />
                  <label htmlFor="insurance" className="text-snow/70 text-sm cursor-pointer">I have professional guide insurance</label>
                </div>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4A017" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h2 className="text-snow font-bold text-xl mb-3">Ready to Submit!</h2>
              <p className="text-snow/60 text-sm max-w-sm mx-auto mb-6">
                Your application will be reviewed by our team within 2-3 business days. You&apos;ll receive an email once verified.
              </p>
              <div className="glass-panel rounded-xl p-4 text-left mb-6 max-w-sm mx-auto">
                <div className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-3">Application Summary</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-snow/50">Name</span><span className="text-snow">{form.fullName || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-snow/50">Category</span><span className="text-snow">{form.category || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-snow/50">Experience</span><span className="text-snow">{form.experience ? `${form.experience} years` : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-snow/50">Daily Rate</span><span className="text-gold">Rs. {form.dailyRate || '—'}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="btn-navy px-5 py-2.5 rounded-xl text-sm disabled:opacity-30"
            >
              ← Back
            </button>
            {currentStep < 5 ? (
              <button
                onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
                className="btn-gold px-6 py-2.5 rounded-xl text-sm font-semibold"
              >
                Continue →
              </button>
            ) : (
              <button className="btn-gold px-6 py-2.5 rounded-xl text-sm font-semibold">
                Submit Application
              </button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
